/**
 * セルと素材の対応、および**素材ごとの GPU 側の実体**を持つ台帳(#168)。
 *
 * 正本は [ADR-0031](../../../docs/adr/0031-source-keyed-upload.md)。
 *
 * ## なぜ切り出すか
 *
 * **WebGL は jsdom で試さない**([ADR-0019](../../../docs/adr/0019-dev-foundation.md))。
 * 参照数の管理を `WebGlRenderer` の中に書くと**検査できない**が、ここは
 * 数え間違いがそのまま目に見える壊れ方になる部分である ——
 *
 * | 間違い | 見え方 |
 * |---|---|
 * | 早く捨てる | **共有している他のセルの絵が消える** |
 * | 捨て残す | テクスチャと `requestVideoFrameCallback` の予約が残る(復号器も) |
 *
 * そこで **GL を知らない形**にして、生成と破棄を差し込みにした。
 * `WebGlRenderer` は `createSourceTexture` と `deleteTexture` を渡すだけになる。
 *
 * ## 鍵
 *
 * - セル側の鍵は文字列(`cellKey`)
 * - 素材側の鍵は**要素そのもの**(オブジェクトの同一性)。
 *   **ファイルの内容では見ない** —— 描画コアはファイルを知らない。
 *   同じファイルから 1 本の要素を作るのは呼び出し側の仕事。
 */
/**
 * セル → 素材 → 実体の 2 段の台帳。
 *
 * **素材は複数のセルから参照されうる**(#152 の分割 / 同じファイルの共有)。
 */
export class UploadRegistry {
    hooks;
    /** セルの鍵 → その上に載っている素材。 */
    cells = new Map();
    /** 素材 → 実体と参照数。 */
    held = new Map();
    constructor(hooks) {
        this.hooks = hooks;
    }
    /**
     * セルに素材を置く。
     *
     * **前に別の素材が載っていたら、それを外す**(参照が 0 になれば捨てる)。
     */
    assign(cellKey, source) {
        const previous = this.cells.get(cellKey);
        if (previous === source) {
            const holder = this.held.get(source);
            // 台帳が食い違っている場合は作り直しに落とす(起こらないはずの経路)。
            if (holder !== undefined)
                return { kind: 'same', entry: holder.entry };
        }
        const shared = this.held.get(source);
        if (shared !== undefined) {
            if (previous !== undefined)
                this.detach(cellKey);
            shared.refs++;
            this.cells.set(cellKey, source);
            return { kind: 'shared', entry: shared.entry };
        }
        /*
         * **作るのが先、外すのが後。**
         *
         * 逆にすると、`create` が投げたときに**前の絵が既に失われている** ——
         * セルが空になったうえで差し替えも失敗する、という二重の損になる。
         * 先に作れば、投げても**前の絵がそのまま残る。**
         */
        const entry = this.hooks.create(source);
        if (previous !== undefined)
            this.detach(cellKey);
        this.held.set(source, { entry, refs: 1 });
        this.cells.set(cellKey, source);
        return { kind: 'created', entry };
    }
    /** セルから素材を外す。**参照が 0 になった素材だけ捨てる。** */
    detach(cellKey) {
        const source = this.cells.get(cellKey);
        if (source === undefined)
            return;
        this.cells.delete(cellKey);
        const holder = this.held.get(source);
        if (holder === undefined)
            return;
        holder.refs--;
        if (holder.refs > 0)
            return;
        this.held.delete(source);
        this.hooks.destroy(holder.entry);
    }
    /** このセルに載っている実体。 */
    entryOf(cellKey) {
        const source = this.cells.get(cellKey);
        if (source === undefined)
            return undefined;
        return this.held.get(source)?.entry;
    }
    /** このセルに載っている素材。 */
    sourceOf(cellKey) {
        return this.cells.get(cellKey);
    }
    /** この枠のセルに 1 つでも素材が載っているか。 */
    hasAnyIn(frameId) {
        const prefix = `${frameId}#`;
        for (const key of this.cells.keys()) {
            if (key.startsWith(prefix))
                return true;
        }
        return false;
    }
    /** すべて捨てる。**実体は 1 つずつ `destroy` する。** */
    clear() {
        for (const holder of this.held.values())
            this.hooks.destroy(holder.entry);
        this.held.clear();
        this.cells.clear();
    }
    /**
     * 保持している実体を順に返す(#174)。
     *
     * **素材ごとに 1 度だけ**(共有していても重複しない)——
     * 「描く必要があるか」を毎フレーム見るために使う。
     */
    entries() {
        return [...this.held.values()].map((holder) => holder.entry);
    }
    /** 保持している**素材の数**(セルの数ではない)。 */
    sourceCount() {
        return this.held.size;
    }
    /** この素材を参照しているセルの数。**持っていなければ 0。** */
    refsOf(source) {
        return this.held.get(source)?.refs ?? 0;
    }
}
//# sourceMappingURL=upload-registry.js.map